import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as seed from "../data/demo/seed";
import { calculateQuoteTotals } from "../lib/calculations";
import type {
  AccountPayable,
  AccountReceivable,
  AiGeneration,
  CatalogItem,
  Client,
  DailyReport,
  Expense,
  Lead,
  Measurement,
  Notification,
  Opportunity,
  Project,
  ProjectDocument,
  ProjectPhoto,
  Quote,
} from "../types/domain";

const id = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
export interface DemoData {
  clients: Client[];
  leads: Lead[];
  opportunities: Opportunity[];
  measurements: Measurement[];
  aiGenerations: AiGeneration[];
  quotes: Quote[];
  projects: Project[];
  expenses: Expense[];
  receivables: AccountReceivable[];
  payables: AccountPayable[];
  notifications: Notification[];
  dailyReports: DailyReport[];
  photos: ProjectPhoto[];
  documents: ProjectDocument[];
  services: CatalogItem[];
  materials: CatalogItem[];
  suppliers: CatalogItem[];
}
const createSeed = (): DemoData => ({
  clients: structuredClone(seed.clients),
  leads: structuredClone(seed.leads),
  opportunities: structuredClone(seed.opportunities),
  measurements: structuredClone(seed.measurements),
  aiGenerations: structuredClone(seed.aiGenerations),
  quotes: structuredClone(seed.quotes),
  projects: structuredClone(seed.projects),
  expenses: structuredClone(seed.expenses),
  receivables: structuredClone(seed.receivables),
  payables: structuredClone(seed.payables),
  notifications: structuredClone(seed.notifications),
  dailyReports: [],
  photos: [
    {
      id: "photo-1",
      projectId: "project-1",
      name: "Sala — preparação das paredes.jpg",
      category: "Durante",
      clientVisible: true,
    },
  ],
  documents: [
    {
      id: "doc-1",
      projectId: "project-1",
      name: "Proposta ORC-2026-0018.pdf",
      category: "Orçamento",
      uploadedAt: "2026-08-14",
    },
  ],
  services: structuredClone(seed.services),
  materials: structuredClone(seed.materials),
  suppliers: structuredClone(seed.suppliers),
});
interface AppState extends DemoData {
  sidebarCollapsed: boolean;
  onboardingSeen: boolean;
  projectsView: "cards" | "table";
  recentPaths: string[];
  setSidebarCollapsed(value: boolean): void;
  completeOnboarding(): void;
  setProjectsView(value: "cards" | "table"): void;
  addRecentPath(path: string): void;
  completeLeadNextAction(leadId: string): void;
  addLead(input: Omit<Lead, "id" | "createdAt" | "stage">): Lead;
  addClient(input: Omit<Client, "id" | "lastActivity" | "status">): Client;
  addOpportunityFromLead(leadId: string): Opportunity | undefined;
  saveMeasurement(measurement: Measurement): void;
  approveGeneration(opportunityId: string): void;
  upsertQuote(quote: Quote): void;
  sendQuote(quoteId: string): void;
  approveQuote(quoteId: string): void;
  convertQuoteToProject(
    quoteId: string,
    input: Pick<Project, "name" | "startDate" | "endDate" | "owner">,
  ): Project | undefined;
  addTask(projectId: string, task: Project["tasks"][number]): void;
  updateTask(projectId: string, task: Project["tasks"][number]): void;
  addDailyReport(report: Omit<DailyReport, "id">): void;
  addExpense(expense: Omit<Expense, "id">): void;
  addPhoto(photo: Omit<ProjectPhoto, "id">): void;
  togglePhotoVisibility(photoId: string): void;
  addDocument(document: Omit<ProjectDocument, "id">): void;
  markNotificationRead(notificationId: string): void;
  markAllNotificationsRead(): void;
  resetDemo(): void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      ...createSeed(),
      sidebarCollapsed: false,
      onboardingSeen: false,
      projectsView: "cards",
      recentPaths: [
        "/obras/project-1/resumo",
        "/clientes/client-1",
        "/orcamentos/quote-1",
      ],
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      completeOnboarding: () => set({ onboardingSeen: true }),
      setProjectsView: (projectsView) => set({ projectsView }),
      addRecentPath: (path) =>
        set({
          recentPaths: [
            path,
            ...get().recentPaths.filter((item) => item !== path),
          ].slice(0, 5),
        }),
      completeLeadNextAction: (leadId) =>
        set({
          leads: get().leads.map((lead) =>
            lead.id === leadId
              ? {
                  ...lead,
                  nextActionDone: true,
                  lastInteractionAt: new Date().toISOString(),
                }
              : lead,
          ),
        }),
      addLead: (input) => {
        const lead: Lead = {
          ...input,
          id: id("lead"),
          createdAt: new Date().toISOString(),
          stage: "Novo",
          code: `LEAD-2026-${String(get().leads.length + 32).padStart(4, "0")}`,
          estimatedValue: input.estimatedValue ?? 0,
          lastInteractionAt: new Date().toISOString(),
          nextAction: "Realizar primeiro contato",
        };
        set({ leads: [lead, ...get().leads] });
        return lead;
      },
      addClient: (input) => {
        const client: Client = {
          ...input,
          id: id("client"),
          lastActivity: new Date().toISOString(),
          status: "Ativo",
        };
        set({ clients: [client, ...get().clients] });
        return client;
      },
      addOpportunityFromLead: (leadId) => {
        const lead = get().leads.find((item) => item.id === leadId);
        if (!lead) return undefined;
        const existingClient = lead.clientId
          ? get().clients.find((item) => item.id === lead.clientId)
          : get().clients.find(
              (item) => item.email.toLowerCase() === lead.email.toLowerCase(),
            );
        const client: Client = existingClient ?? {
          id: id("client"),
          name: lead.name,
          phone: lead.phone,
          email: lead.email,
          document: "Não informado",
          city: lead.city,
          address: `${lead.city} · endereço a confirmar`,
          notes: lead.notes,
          status: "Ativo",
          lastActivity: new Date().toISOString(),
        };
        const opportunity: Opportunity = {
          id: id("opp"),
          clientId: client.id,
          leadId,
          title: lead.service,
          service: lead.service,
          stage: "Solicitação",
          value: 0,
          createdAt: new Date().toISOString(),
        };
        set({
          clients: existingClient ? get().clients : [client, ...get().clients],
          opportunities: [opportunity, ...get().opportunities],
          leads: get().leads.map((item) =>
            item.id === leadId
              ? { ...item, opportunityId: opportunity.id, clientId: client.id }
              : item,
          ),
        });
        return opportunity;
      },
      saveMeasurement: (measurement) =>
        set({
          measurements: [
            ...get().measurements.filter((item) => item.id !== measurement.id),
            measurement,
          ],
        }),
      approveGeneration: (opportunityId) =>
        set({
          aiGenerations: get().aiGenerations.map((item) =>
            item.opportunityId === opportunityId
              ? {
                  ...item,
                  status: "success",
                  approvedAt: new Date().toISOString(),
                }
              : item,
          ),
          notifications: [
            {
              id: id("not"),
              title: "Projeto visual aprovado",
              description:
                "A versão visual foi aprovada e está pronta para orçamento.",
              type: "Nova simulação",
              read: false,
              createdAt: new Date().toISOString(),
              path: `/ai-studio/${opportunityId}`,
            },
            ...get().notifications,
          ],
        }),
      upsertQuote: (quote) =>
        set({
          quotes: [
            quote,
            ...get().quotes.filter((item) => item.id !== quote.id),
          ],
        }),
      sendQuote: (quoteId) =>
        set({
          quotes: get().quotes.map((quote) =>
            quote.id === quoteId
              ? {
                  ...quote,
                  status: "Enviado",
                  sentAt: new Date().toISOString(),
                }
              : quote,
          ),
        }),
      approveQuote: (quoteId) => {
        const quote = get().quotes.find((item) => item.id === quoteId);
        if (!quote) return;
        const clientName =
          get().clients.find((item) => item.id === quote.clientId)?.name ??
          "O cliente";
        set({
          quotes: get().quotes.map((item) =>
            item.id === quoteId
              ? {
                  ...item,
                  status: "Aprovado",
                  approvedAt: new Date().toISOString(),
                }
              : item,
          ),
          notifications: [
            {
              id: id("not"),
              title: "Orçamento aprovado",
              description: `${clientName} aprovou ${quote.code}.`,
              type: "Orçamento aprovado",
              read: false,
              createdAt: new Date().toISOString(),
              path: `/orcamentos/${quoteId}`,
            },
            ...get().notifications,
          ],
        });
      },
      convertQuoteToProject: (quoteId, input) => {
        const quote = get().quotes.find(
          (item) => item.id === quoteId && item.status === "Aprovado",
        );
        if (!quote) return undefined;
        const linkedProject = get().projects.find(
          (item) => item.quoteId === quoteId,
        );
        if (linkedProject) {
          set({
            quotes: get().quotes.map((item) =>
              item.id === quoteId
                ? { ...item, projectId: linkedProject.id }
                : item,
            ),
          });
          return linkedProject;
        }
        const project: Project = {
          id: id("project"),
          code: `OBR-2026-${String(get().projects.length + 15).padStart(4, "0")}`,
          clientId: quote.clientId,
          quoteId,
          address:
            get().clients.find((item) => item.id === quote.clientId)?.address ??
            "Endereço a definir",
          status: "Planejamento",
          contractValue: calculateQuoteTotals(quote).total,
          received: 0,
          physicalProgress: 0,
          tasks: [],
          ...input,
        };
        set({
          projects: [project, ...get().projects],
          quotes: get().quotes.map((item) =>
            item.id === quoteId ? { ...item, projectId: project.id } : item,
          ),
        });
        return project;
      },
      addTask: (projectId, task) =>
        set({
          projects: get().projects.map((project) =>
            project.id === projectId
              ? { ...project, tasks: [task, ...project.tasks] }
              : project,
          ),
        }),
      updateTask: (projectId, task) =>
        set({
          projects: get().projects.map((project) =>
            project.id === projectId
              ? {
                  ...project,
                  tasks: project.tasks.map((item) =>
                    item.id === task.id ? task : item,
                  ),
                }
              : project,
          ),
        }),
      addDailyReport: (report) =>
        set({
          dailyReports: [{ ...report, id: id("rdo") }, ...get().dailyReports],
        }),
      addExpense: (expense) =>
        set({
          expenses: [{ ...expense, id: id("expense") }, ...get().expenses],
        }),
      addPhoto: (photo) =>
        set({ photos: [{ ...photo, id: id("photo") }, ...get().photos] }),
      togglePhotoVisibility: (photoId) =>
        set({
          photos: get().photos.map((photo) =>
            photo.id === photoId
              ? { ...photo, clientVisible: !photo.clientVisible }
              : photo,
          ),
        }),
      addDocument: (document) =>
        set({
          documents: [{ ...document, id: id("doc") }, ...get().documents],
        }),
      markNotificationRead: (notificationId) =>
        set({
          notifications: get().notifications.map((item) =>
            item.id === notificationId ? { ...item, read: true } : item,
          ),
        }),
      markAllNotificationsRead: () =>
        set({
          notifications: get().notifications.map((item) => ({
            ...item,
            read: true,
          })),
        }),
      resetDemo: () =>
        set({
          ...createSeed(),
          sidebarCollapsed: false,
          onboardingSeen: false,
          projectsView: "cards",
          recentPaths: [],
        }),
    }),
    {
      name: "empreitaflow-demo-v1",
      partialize: ({ sidebarCollapsed, ...data }) => ({
        sidebarCollapsed,
        ...data,
      }),
    },
  ),
);

export const resetAppStoreForTests = () =>
  useAppStore.setState({
    ...createSeed(),
    sidebarCollapsed: false,
    onboardingSeen: false,
    projectsView: "cards",
    recentPaths: [],
  });
